import { useCallback, useRef, useState } from "react";
import { sendChat, stopChat } from "../api/client";
import { listConversations, getTurns, startNewConversation } from "../api/conversations";
import { HttpError } from "../api/errors";
import type { ProgressKind, StreamEvent, TurnRow } from "../api/types";

export type ProgressEntry = {
  id: string;
  kind: ProgressKind;
  label: string;
  ts: number;
};

export type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  streaming: boolean;
  error?: string;
  progress?: ProgressEntry[];
  interrupted?: boolean;
};

export type UseChatStream = {
  messages: ChatMessage[];
  send: (text: string) => Promise<void>;
  stop: () => Promise<void>;
  isStreaming: boolean;
  isLoadingHistory: boolean;
  authError: boolean;
  clearAuthError: () => void;
  /**
   * Last non-auth error from history-load or chat-send. Preserves the original
   * Error instance (including HttpError) so consumers can branch on
   * `error instanceof HttpError && error.status === 403` for the forbidden UI.
   */
  error: Error | null;
  clearError: () => void;
  loadLatestHistory: () => Promise<void>;
  loadConversation: (conversationId: number) => Promise<void>;
  startNew: () => Promise<void>;
  currentConversationId: number | null;
};

const AUTH_ERROR_MARKERS = new Set(["unauthorized", "no token configured"]);

function nextId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function turnsToMessages(turns: TurnRow[]): ChatMessage[] {
  return turns.map((t) => ({
    id: `t-${t.id}`,
    role: t.role,
    content: t.content,
    streaming: false,
  }));
}

function classifyError(e: unknown): { isAuth: boolean; err: Error } {
  const err = e instanceof Error ? e : new Error(String(e));
  // 401-level HttpError counts as auth too, but for back-compat we also keep
  // the message-marker test so non-HttpError "unauthorized" surfaces are caught.
  const isHttp401 = err instanceof HttpError && err.status === 401;
  const isAuth = isHttp401 || AUTH_ERROR_MARKERS.has(err.message);
  return { isAuth, err };
}

export function useChatStream(project: string): UseChatStream {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [currentConversationId, setCurrentConversationId] = useState<number | null>(null);
  const stopRequestedRef = useRef(false);

  const clearAuthError = useCallback(() => setAuthError(false), []);
  const clearError = useCallback(() => setError(null), []);

  const stop = useCallback(async () => {
    if (!isStreaming) return;
    stopRequestedRef.current = true;
    // Optimistic: flip the streaming message to interrupted AND unlock the
    // composer immediately. The lingering SSE stream will still drain in the
    // background but the user can already type their next prompt.
    setMessages((prev) =>
      prev.map((m) =>
        m.role === "assistant" && m.streaming
          ? { ...m, interrupted: true, streaming: false }
          : m,
      ),
    );
    setIsStreaming(false);
    try {
      await stopChat(project);
    } catch (e) {
      const { isAuth, err } = classifyError(e);
      if (isAuth) setAuthError(true);
      else setError(err);
    }
  }, [project, isStreaming]);

  const loadLatestHistory = useCallback(async () => {
    setIsLoadingHistory(true);
    setError(null);
    try {
      const convs = await listConversations(project);
      if (convs.length === 0) {
        setMessages([]);
        setCurrentConversationId(null);
        return;
      }
      // List is returned newest-first by the API; pick [0] as the latest.
      const latest = convs[0];
      const turns = await getTurns(project, latest.id);
      setMessages(turnsToMessages(turns));
      setCurrentConversationId(latest.id);
    } catch (e) {
      const { isAuth, err } = classifyError(e);
      if (isAuth) setAuthError(true);
      else setError(err);
      // Do NOT clear messages on failure — fall back to whatever was there
      // so the user can keep sending in a fresh in-memory transcript.
    } finally {
      setIsLoadingHistory(false);
    }
  }, [project]);

  const loadConversation = useCallback(
    async (conversationId: number) => {
      setIsLoadingHistory(true);
      setError(null);
      try {
        const turns = await getTurns(project, conversationId);
        setMessages(turnsToMessages(turns));
        setCurrentConversationId(conversationId);
      } catch (e) {
        const { isAuth, err } = classifyError(e);
        if (isAuth) setAuthError(true);
        else setError(err);
      } finally {
        setIsLoadingHistory(false);
      }
    },
    [project],
  );

  const startNew = useCallback(async () => {
    setError(null);
    try {
      // POST /conversations/:project also sets it active on the daemon side,
      // so the next send() will persist into this new conversation.
      const row = await startNewConversation(project);
      setMessages([]);
      setCurrentConversationId(row.id);
    } catch (e) {
      const { isAuth, err } = classifyError(e);
      if (isAuth) setAuthError(true);
      else setError(err);
    }
  }, [project]);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isStreaming) return;

      const userMsg: ChatMessage = {
        id: nextId(),
        role: "user",
        content: trimmed,
        streaming: false,
      };
      const assistantId = nextId();
      const assistantMsg: ChatMessage = {
        id: assistantId,
        role: "assistant",
        content: "",
        streaming: true,
      };

      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setIsStreaming(true);
      setError(null);
      stopRequestedRef.current = false;

      const finalizeAssistant = (patch: Partial<ChatMessage>) => {
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? { ...m, ...patch, streaming: false } : m)),
        );
      };

      // NOTE: We do not pass conversation_id in /chat for v1. The daemon owns
      // the active-conversation pointer per (user, project). loadLatestHistory
      // only displays the latest conv which is also what the daemon will
      // continue writing into. Loading an OLDER conversation is view-only;
      // sending a new message still goes to the daemon-active (latest) conv.
      // Plan 07 may extend the daemon to accept conversation_id for true resume.
      try {
        for await (const ev of sendChat({ project, message: trimmed }) as AsyncIterable<StreamEvent>) {
          if (ev.type === "token") {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, content: m.content + ev.text } : m,
              ),
            );
          } else if (ev.type === "progress") {
            const entry: ProgressEntry = {
              id: nextId(),
              kind: ev.kind,
              label: ev.label,
              ts: Date.now(),
            };
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, progress: [...(m.progress ?? []), entry] } : m,
              ),
            );
          } else if (ev.type === "done") {
            finalizeAssistant(stopRequestedRef.current ? { interrupted: true } : {});
          } else if (ev.type === "error") {
            if (AUTH_ERROR_MARKERS.has(ev.error)) {
              setAuthError(true);
            } else if (!stopRequestedRef.current) {
              setError(new Error(ev.error));
            }
            finalizeAssistant(
              stopRequestedRef.current ? { interrupted: true } : { error: ev.error },
            );
          }
        }
        // Stream ended without a "done" frame (e.g. session killed via /stop).
        if (stopRequestedRef.current) {
          finalizeAssistant({ interrupted: true });
        }
      } catch (e) {
        const { isAuth, err } = classifyError(e);
        if (isAuth) setAuthError(true);
        else if (!stopRequestedRef.current) setError(err);
        finalizeAssistant(
          stopRequestedRef.current ? { interrupted: true } : { error: err.message },
        );
      } finally {
        setIsStreaming(false);
        stopRequestedRef.current = false;
      }
    },
    [project, isStreaming],
  );

  return {
    messages,
    send,
    stop,
    isStreaming,
    isLoadingHistory,
    authError,
    clearAuthError,
    error,
    clearError,
    loadLatestHistory,
    loadConversation,
    startNew,
    currentConversationId,
  };
}
